<template>
    <template-card-v2 :oneLog="oneLog" :linkedtype="linkedtype" :linkedid="linkedid">
        <template #label>
            <person-list-light
                :list="oneLog.staffs.params.list"
                placeholderSingular="Collaborateur"
                placeholderPlural="Collaborateurs"
            >
            </person-list-light>
        </template>
        <template #icon>
            <CompassObject object="task" size="small" />
        </template>

        <template #content>
            <div v-show="oneLog.canSee" class="task-card-content__container">
                <!-- LABEL -->
                <div class="task-card-information__container">
                    <div class="flex-column">
                        <div class="task-card-date__container">
                            <CompassIcon size="small" icon="calendar-fill" />
                            <span class="typographies-paragraph-medium-semibold">
                                {{ $t.__('common.labels.due_date', date) }}
                            </span>
                            <BadgeActivityStatus v-if="isLate" :tag="activityStatus" />
                        </div>

                        <CompassTag
                            v-if="oneLog.label"
                            type="dot"
                            size="small"
                            :color="!!oneLog.labelColor.length ? 'custom' : 'yellow'"
                            :customColor="`#${oneLog.labelColor}`"
                            class="task-card-tag"
                            v-on="!oneLog.title ? { click: () => previewTask(oneLog) } : {}"
                        >
                            {{ oneLog.label }}
                        </CompassTag>
                    </div>

                    <div class="task-card-buttons__container">
                        <TimelineActivityRescheduleButton
                            v-if="!isComplete"
                            :activity="activityForReschedule"
                        />
                        <CompassButton
                            :label="taskActionLabel"
                            type="secondary"
                            :class="['task-card-action__container', taskActionClass]"
                            leftIcon="check"
                            @click="toggleCheckTask"
                        ></CompassButton>
                    </div>
                </div>
                <span
                    v-if="oneLog.title"
                    class="typographies-paragraph-medium-semibold task-card-title"
                    @click="previewTask(oneLog)"
                >
                    {{ oneLog.title }}
                </span>

                <div
                    v-if="oneLog.description"
                    class="typographies-paragraph-medium-regular task-card__content rich-text-style"
                    :class="editorClass"
                    v-html="addBrToEmptyPragraphs(oneLog.description)"
                />
            </div>
        </template>
    </template-card-v2>
</template>

<script>
import BadgeActivityStatus from 'vcomponents/utilities/badge/BadgeActivityStatus.vue';
import TemplateCardV2 from './TemplateCardV2.vue';
import TimelineCardMixin from './TimelineCardMixin.js';
import PersonListLight from 'vcomponents/utilities/PersonListLight.vue';
import Feature from 'Services/Feature';
import { createNamespacedHelpers } from 'vuex';
import TimelineActivityRescheduleButton from 'vcomponents/timeline/TimelineActivityRescheduleButton.vue';
import { addBrToEmptyPragraphs, editorClass } from 'Helpers/richTextHelper.js';

const { mapState: mapStateUser } = createNamespacedHelpers('user');

export default {
    components: {
        TemplateCardV2,
        PersonListLight,
        BadgeActivityStatus,
        TimelineActivityRescheduleButton,
    },
    mixins: [TimelineCardMixin],
    data() {
        return {
            activityForReschedule: {
                activityId: this.oneLog.objectid,
                type: this.oneLog.objecttype,
                date: moment.unix(this.oneLog.timestamp),
            },
        };
    },
    computed: {
        ...mapStateUser({
            userForename: 'forename',
            userName: 'name',
        }),
        editorClass,
        isLate() {
            return (
                moment().isAfter(moment.unix(this.oneLog.start)) &&
                this.oneLog.status !== 'complete'
            );
        },
        isComplete() {
            return this.oneLog.status === 'complete';
        },
        date() {
            return this.$filters.dateFormatter(
                this.oneLog.end,
                'NORMAL_DATE_TIME_WITH_DAY',
                false,
            );
        },
        taskActionLabel() {
            return this.isComplete ? this.$l10n.__('Terminé') : this.$l10n.__('Terminer');
        },
        taskActionClass() {
            return {
                'is-success': this.isComplete,
            };
        },
        activityStatus() {
            return this.isLate ? { type: 'error', label: this.$t.__('common.labels.isLate') } : {};
        },
    },
    methods: {
        addBrToEmptyPragraphs,
        previewTask(task) {
            if (['delivery', 'proforma'].includes(this.oneLog.relatedtype)) {
                Activities.openOldTaskDetail(
                    this.oneLog.objectid,
                    this.oneLog.can_edit,
                    this.oneLog.timestamp,
                    this.oneLog.relatedtype,
                );
                return;
            }
            if (Feature.isEnabled('activity-new-creation-panel')) {
                Popup.openPopup({
                    key: 'activityDetailPanel',
                    props: {
                        activityDetailDisplayed: {
                            id: this.oneLog.objectid,
                            type: 'task',
                            related: [
                                {
                                    id: +(this.oneLog.relatedid ?? 0),
                                    type: this.oneLog.relatedtype,
                                    subtype: this.getCardType,
                                },
                            ],
                        },
                        callback: () =>
                            EventBus.emit('timeline:refreshLog', {
                                objectType: this.oneLog.objecttype,
                                objectId: this.oneLog.objectid,
                            }),
                        callbackOnDelete: () => EventBus.emit('timeline:refresh'),
                        callbackOnCreate: (data) => {
                            EventBus.emit('timeline:processAndAdd', {
                                entity: 'task',
                                event: 'task.created',
                                objectid: data.id,
                            });
                        },
                    },
                });
            } else {
                const can_edit = task.can_edit ? 'Y' : 'N';
                Agenda.cardForm(
                    task.objectid,
                    0,
                    'task',
                    can_edit,
                    false,
                    task.start,
                    task.end,
                    'N',
                    task.objecttype,
                );
            }
        },
        toggleCheckTask(e) {
            const button = e.target.tagName === 'SPAN' ? e.target.parentElement : e.target;

            if (this.oneLog.status === 'ok') {
                this.oneLog.status = 'complete';
                button.blur();
                axios.post(
                    this.$router.resolve({
                        name: 'agenda.event.action',
                        params: { action: 'complete', id: this.oneLog.objectid },
                    }).href,
                );
            } else {
                this.oneLog.status = 'ok';
                button.blur();
                axios.post(
                    this.$router.resolve({
                        name: 'agenda.event.action',
                        params: { action: 'uncomplete', id: this.oneLog.objectid },
                    }).href,
                );
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.task-card {
    &-content__container {
        display: grid;
    }

    &-title {
        cursor: pointer;
        margin-top: var(--spacing-1);
        margin-bottom: var(--spacing-0);
    }
    &-tag {
        margin-bottom: var(--spacing-1);
    }
    &__content {
        margin: var(--spacing-0) 0 0;
    }
    &-action__container {
        height: max-content;
        white-space: nowrap;
        &.is-success {
            border-color: var(--colors-common-border-success-darker);
            color: var(--colors-common-text-success-hover);
            &:hover {
                background-color: var(--colors-common-background-success-subtle);
            }
            &,
            &:deep(h6) {
                color: var(--colors-common-text-success-hover);
            }
        }
    }
    &-information__container {
        display: flex;
        gap: var(--spacing-1);
        justify-content: space-between;
        align-items: flex-start;
    }

    &-date__container {
        display: flex;
        gap: var(--spacing-0);
        align-items: center;
    }

    &-buttons__container {
        display: flex;
        gap: var(--spacing-3);
    }
}
</style>
